import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Backlog } from '../util-cmps/backlog'
import { useDraggable } from '../../hooks/use-draggable'
import { useEscapeKey } from '../../hooks/use-escape-key'
import { useScrollLock } from '../../hooks/use-scroll-lock'
import { newHttpService } from '../../services/new-http-service'
import { utilService } from '../../services/util-service'

export interface AuditControlAuditLogEntry {
    id: number
    company_name: string
    actor_email: string
    actor_name: string | null
    action: string
    resource_type: string
    resource_id: string
    old_value: Record<string, any> | any[] | null
    new_value: Record<string, any> | any[] | null
    reason: string | null
    status: string
    ip_address: string | null
    request_id: string | null
    created_at: string
}

interface AuditControlAuditHistoryResponse {
    items: AuditControlAuditLogEntry[]
    total: number
}

interface AuditControlAuditHistoryModalProps {
    isOpen: boolean
    companyName: string
    auditControlId: string
    onClose: () => void
}

const formatTimestamp = (value: string) => utilService.formatDateTime(value) || '—'

const formatActor = (entry: AuditControlAuditLogEntry) => entry.actor_name?.trim() || entry.actor_email || 'Unknown'

const formatAuditSummary = (entry: AuditControlAuditLogEntry) =>
    `Latest changed by ${formatActor(entry)} · ${formatTimestamp(entry.created_at)}`

const formatJson = (value: Record<string, any> | any[] | null) => {
    if (value == null) return '—'
    if (Array.isArray(value) && value.length === 0) return '—'
    if (!Array.isArray(value) && Object.keys(value).length === 0) return '—'
    return JSON.stringify(value, null, 2)
}

export const getAuditControlSummary = formatAuditSummary

export const AuditControlAuditHistoryModal: React.FC<AuditControlAuditHistoryModalProps> = ({
    isOpen,
    companyName,
    auditControlId,
    onClose,
}) => {
    useEscapeKey(onClose)
    useScrollLock()
    const { modalRef, onHeaderMouseDown } = useDraggable<HTMLElement>()
    const [history, setHistory] = useState<AuditControlAuditLogEntry[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [hasError, setHasError] = useState(false)

    useEffect(() => {
        if (!isOpen) return
        let active = true
        setIsLoading(true)
        setHasError(false)
        newHttpService.get('/api/backoffice/audit-log/history', {
            company_name: companyName,
            resource_type: 'audit_control',
            resource_id: auditControlId,
            limit: 100,
        })
            .then((res: AuditControlAuditHistoryResponse | any) => {
                if (!active) return
                setHistory(Array.isArray(res?.items) ? res.items : [])
            })
            .catch(() => {
                if (!active) return
                setHasError(true)
                setHistory([])
            })
            .finally(() => {
                if (!active) return
                setIsLoading(false)
            })
        return () => {
            active = false
        }
    }, [auditControlId, companyName, isOpen])

    const orderedHistory = useMemo(() => [...history].sort((a, b) => {
        const aTs = new Date(a.created_at).getTime()
        const bTs = new Date(b.created_at).getTime()
        if (aTs !== bTs) return bTs - aTs
        return b.id - a.id
    }), [history])

    if (!isOpen) return null

    return createPortal(
        <>
            <section ref={modalRef} className="form-modal glue-trigger-history-modal" style={{ zIndex: 320 }} role="dialog" aria-modal="true" aria-label="Audit control history">
                <header onMouseDown={onHeaderMouseDown}>
                    <h2>Audit control history</h2>
                    <button className="close-btn" onClick={onClose} aria-label="Close">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </header>

                <div className="modal-form-body glue-trigger-history-body">
                    <div className="form-section-card">
                        <div className="form-section-label">Audit Control</div>
                        <div className="glue-trigger-history-summary">
                            <strong>Control #{auditControlId}</strong>
                            <span>Audit events for this control are listed below, newest first.</span>
                        </div>
                    </div>

                    <div className="form-section-card">
                        <div className="form-section-label">Changes</div>
                        {isLoading ? (
                            <div className="glue-trigger-history-empty">Loading history...</div>
                        ) : hasError ? (
                            <div className="glue-trigger-history-empty glue-trigger-history-empty--error">
                                Failed to load audit history for this control.
                            </div>
                        ) : orderedHistory.length === 0 ? (
                            <div className="glue-trigger-history-empty">No audit history found yet.</div>
                        ) : (
                            <div className="glue-trigger-history-list">
                                {orderedHistory.map(entry => (
                                    <div key={entry.id} className="glue-trigger-history-item">
                                        <div className="glue-trigger-history-item-head">
                                            <div>
                                                <div className="glue-trigger-history-item-action">{entry.action}</div>
                                                <div className="glue-trigger-history-item-meta">{formatAuditSummary(entry)}</div>
                                            </div>
                                            <span className={`glue-trigger-history-status glue-trigger-history-status--${entry.status.toLowerCase()}`}>
                                                {entry.status}
                                            </span>
                                        </div>
                                        {entry.reason && <div className="glue-trigger-history-item-reason">{entry.reason}</div>}
                                        <div className="glue-trigger-history-item-grid">
                                            <div>
                                                <span>Old value</span>
                                                <pre>{formatJson(entry.old_value)}</pre>
                                            </div>
                                            <div>
                                                <span>New value</span>
                                                <pre>{formatJson(entry.new_value)}</pre>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>
            <Backlog zIndex={319} onCloseBacklog={onClose} />
        </>,
        document.body,
    )
}
